import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BuzerIcon } from "../../BuzerIcons";
import { Avatar, Chk, FriendChip, FriendPickerHint, Glyph, IconPicker, Modal, ProfileBody, profileTier, tierCardStyle, liteCardStyle, ProUpsell, StatMiniTile, useModalBackClose, useProfileModal, useScrollLock, DateTimeField } from "../../components/ui";
import { Segmented, Button, IconButton, Tag } from "../../components/ui/ds";
import { usePoll } from "../../hooks/usePoll";
import { Icon, TI } from "../../icons";
import { api } from "../../lib/api";
import { remindersForMonth } from "../../lib/calendar";
import { BUZERAK_LINES, OWNER_STYLE, REPEAT_SHORT } from "../../lib/constants";
import { WEEKDAYS_SK, chatDayHeading, chatTime, todayKey, ymd } from "../../lib/format";
import { session } from "../../lib/session";
import { applyAppTheme, getAppTheme } from "../../lib/theme";
import { st } from "../../styles.js";
import { ChallengesTab } from "../challenges/ChallengesTab";
import { PrehladSwitch } from "../buzer/PrehladSwitch";

// ─────────────────────────────────────────────────────────────
//  Formulár pre zdieľanú udalosť (vytvoriť / upraviť)
// ─────────────────────────────────────────────────────────────
export function SharedEventForm({ fid, event, onSaved, onCancel }) {
  const editing = !!event;
  const [icon, setIcon] = useState(event ? (event.icon || "calendar") : "calendar");
  const [iconPick, setIconPick] = useState(false);  // v22.4 — proper icon picker (was raw text field)
  const [label, setLabel] = useState(event ? event.label : "");
  const [desc, setDesc] = useState(event ? (event.desc || "") : "");
  const [due, setDue] = useState(event && event.due_at ? event.due_at.replace(" ", "T") : "");
  const [end, setEnd] = useState(event && event.end_at ? event.end_at.replace(" ", "T") : "");  // v22 — Koniec
  const [repeat, setRepeat] = useState(event ? (event.repeat || "none") : "none");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit(e) {
    e.preventDefault();
    if (!label.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setBusy(true);
    try {
      const body = { icon, label, desc, due_at: due ? due.replace("T", " ") : "", end_at: (due && end) ? end.replace("T", " ") : "", repeat, notify };
      if (editing) await api("/api/shared/events/" + event.id, { method: "PATCH", body });
      else await api("/api/shared/" + fid + "/events", { method: "POST", body });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <form onSubmit={submit} style={{ ...st.card, background: "var(--surface)", marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={() => setIconPick(true)} aria-label="Vyber ikonu"
          style={{ ...st.field, width: 56, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text)" }}>
          <Glyph icon={icon || "calendar"} size={22} />
        </button>
        <input className="field" style={{ ...st.field, flex: 1 }} placeholder="Názov udalosti"
          value={label} onChange={(e) => setLabel(e.target.value)} required autoFocus />
      </div>
      {iconPick && (
        <Modal title="Vyber ikonu" icon="palette" onClose={() => setIconPick(false)} maxWidth={420}>
          <IconPicker value={icon} onPick={(nm) => { setIcon(nm); setIconPick(false); }} />
        </Modal>
      )}
      <input className="field" style={st.field} placeholder="Poznámka"
        value={desc} onChange={(e) => setDesc(e.target.value)} />
      {/* v26.3 §1 — kalendár + čas namiesto natívneho datetime-local */}
      <label style={st.inlineLabel}><span>Termín</span>
        <DateTimeField value={due} onChange={setDue} label="Termín" allowClear /></label>
      {due && (
        <label style={st.inlineLabel}><span>Koniec</span>
          <DateTimeField value={end} onChange={setEnd} min={due} label="Koniec" allowClear timeOptional /></label>
      )}
      <label style={st.inlineLabel}><span>Opakovať</span>
        <select className="field" style={{ ...st.field, width: 150 }}
          value={repeat} onChange={(e) => setRepeat(e.target.value)}>
          <option value="none">Jednorázovo</option>
          <option value="daily">Každý deň</option>
          <option value="weekly">Každý týždeň</option>
          <option value="monthly">Každý mesiac</option>
          <option value="yearly">Každý rok</option>
        </select></label>
      {!editing && (
        <button type="button" onClick={() => setNotify((v) => !v)} className="chip"
          style={{ ...st.chip, ...(notify ? st.chipOn : {}) }}>
          {notify ? <Chk /> : null}Upozorniť priateľa + pridať do Nezabudni
        </button>
      )}
      {err && <div style={st.err}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={onCancel} style={st.cancelBtn}>Zrušiť</button>
        <button type="submit" disabled={busy} style={{ ...st.primaryBtn, flex: 1, ...(!label.trim() ? { opacity: 0.6, filter: "grayscale(1)" } : {}) }}>{busy ? "…" : editing ? "Uložiť" : "Pridať"}</button>
      </div>
    </form>
  );
}
export function SharedCalendar({ fid, friendName, onBack }) {
  const now = new Date();
  const [data, setData] = useState(null);
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [sel, setSel] = useState(todayKey());
  const [form, setForm] = useState(null);  // null | "new" | event object
  const load = useCallback(async () => { setData(await api("/api/shared/" + fid)); }, [fid]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div style={{ display: "flex", justifyContent: "center", padding: 30 }}><div className="spin" /></div>;

  const combined = [
    ...data.events, ...data.my_shared, ...data.friend_shared,
  ];
  const byDay = remindersForMonth(combined, view.y, view.m);
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const lead = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const monthName = new Date(view.y, view.m, 1).toLocaleDateString("sk-SK", { month: "long", year: "numeric" });
  const tKey = todayKey();
  function shift(delta) { setView((p) => { const d = new Date(p.y, p.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; }); }
  const selList = byDay[sel] || [];
  const selPretty = new Date(sel + "T00:00:00").toLocaleDateString("sk-SK", { weekday: "long", day: "numeric", month: "long" });
  async function delEvent(id) { if (!confirm("Odstrániť udalosť?")) return; await api("/api/shared/events/" + id, { method: "DELETE" }); load(); }

  return (
    <>
      <div style={st.topbar}>
        <button onClick={onBack} style={st.logout}>‹ Priatelia</button>
        <span style={st.hello}>🤝 {friendName}</span>
      </div>
      {form ? (
        <SharedEventForm fid={fid} event={form === "new" ? null : form}
          onSaved={() => { setForm(null); load(); }} onCancel={() => setForm(null)} />
      ) : (
        <button onClick={() => setForm("new")} style={st.addToggle}><BuzerIcon name="add" size={16} /> Pridať zdieľanú udalosť</button>
      )}

      <div style={{ ...st.calHead, marginTop: 14 }}>
        <IconButton variant="surface" size="sm" aria-label="Predchádzajúci mesiac" onClick={() => shift(-1)}><Icon name="chevron" size={15} color="currentColor" style={{ transform: "rotate(180deg)" }} /></IconButton>
        <span style={st.calTitle}>{monthName}</span>
        <IconButton variant="surface" size="sm" aria-label="Nasledujúci mesiac" onClick={() => shift(1)}><Icon name="chevron" size={15} color="currentColor" /></IconButton>
      </div>
      <div style={st.calGrid}>
        {WEEKDAYS_SK.map((w) => <div key={w} style={st.calDow}>{w}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={"e" + i} style={st.calEmpty} />;
          const key = ymd(view.y, view.m, d);
          const items = byDay[key] || [];
          return (
            <button key={key} onClick={() => setSel(key)}
              style={{ ...st.calCell, ...(items.length ? st.calCellHas : {}),
                ...(key === tKey ? st.calCellToday : {}), ...(key === sel ? st.calCellSel : {}) }}>
              <span style={{ ...st.calDayNum, ...(key === tKey ? st.calDayNumToday : {}) }}>{d}</span>
              <span style={st.calIcons}>
                {items.slice(0, 3).map((r, j) => <span key={j} style={st.calIcon}><Glyph icon={r.icon} fallback="bell" size={13} /></span>)}
                {items.length > 3 && <span style={st.calMore}>+{items.length - 3}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ ...st.progressHead, marginTop: 20 }}>
        <span className="bz-subhead"><TI name="calendar" color="var(--info)" />{selPretty}</span>
        <span style={st.progressPct}>{selList.length}</span>
      </div>
      <div style={st.taskList}>
        {selList.map((r, i) => {
          const o = OWNER_STYLE[r.owner] || OWNER_STYLE.shared;
          return (
            <div key={(r.owner) + "-" + r.id + "-" + i} className="task" style={{ ...st.task, background: o.bg }}>
              <span style={st.taskIcon}><Glyph icon={r.icon} fallback="bell" /></span>
              <span style={st.taskText}>
                <span style={st.taskLabel}>{r.label}
                  <span style={{ ...st.boostTag, background: "#fff", color: o.color }}>{o.label}</span></span>
                <span style={st.taskDesc}>
                  {r.time ? <><BuzerIcon name="clock" size={11} color="var(--muted)" style={{ verticalAlign: "-2px", marginRight: 3 }} />{r.time}</> : "celý deň"}
                  {r.repeat !== "none" ? " · " + (REPEAT_SHORT[r.repeat] || "") : ""}
                  {r.desc ? " · " + r.desc : ""}
                </span>
              </span>
              {r.editable && <button onClick={() => setForm(r)} style={st.iconBtn} title="Upraviť"><Icon name="edit" size={16} color="currentColor" /></button>}
              {r.editable && <button onClick={() => delEvent(r.id)} style={st.del} title="Odstrániť">×</button>}
            </div>
          );
        })}
      </div>
      {selList.length === 0 && <div style={st.empty}><BuzerIcon name="leaf" size={18} color="var(--muted)" style={{ verticalAlign: "-3px", marginRight: 6 }} />V tento deň nič</div>}
      <div style={st.footer}>Zdieľané udalosti môžete upravovať obaja. Pripomienky označené „👁 zdieľať" sa tu zobrazia len na čítanie.</div>
    </>
  );
}
// ─────────────────────────────────────────────────────────────
//  TAB: Priatelia (pozvánky + zdieľané kalendáre)
// ─────────────────────────────────────────────────────────────
// mode="chat" → tapping a friend opens a chat; mode="calendar" → opens the shared calendar.
// preloaded = ak dostane dáta zvonka (napr. Prehľad ich už načítal), nefetchuje znova;
// hideHeader = nadpis dáva obal (napr. modal má vlastný titul).
export function LeagueSection({ onOpenProfile, onMessage, preloaded = null, hideHeader = false }) {
  const [fetched, setFetched] = useState(null);
  useEffect(() => { if (preloaded) return; api("/api/league").then(setFetched).catch(() => {}); }, [preloaded]);
  const data = preloaded || fetched;
  if (!data || (data.entries || []).length <= 1) return null;  // needs friends
  return (
    <>
      {!hideHeader && (
        <div style={{ ...st.progressHead, marginTop: 20 }}>
          <span className="bz-subhead"><TI name="leaderboard" color="var(--honey)" />Rebríček týždňa</span>
          <span style={st.progressPct}>skóre = body × úspešnosť</span>
        </div>
      )}
      <div style={st.taskList}>
        {data.entries.map((e) => {
          const clickable = !!onOpenProfile;
          return (
          // v32.21 — rebríček = zoznam priateľov; klik na listu otvorí verejný profil (modal).
          // Karta: vľavo poradie + avatar (vertikálne centrované), stred = meno/bar/metriky,
          // vpravo skóre hore a ikona správy dole (pravý stĺpec sa naťahuje na výšku karty).
          <div key={e.id} className="task" onClick={clickable ? () => onOpenProfile(e) : undefined}
            style={{ ...st.task, alignItems: "center", gap: 12, padding: "14px 16px", cursor: clickable ? "pointer" : "default", ...(e.is_me ? { background: "var(--ember-soft)", borderRadius: 18 } : {}) }}>
            {/* v18 §9 — unified rebríček rank badge (1 = filled accent) */}
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 12, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 16,
              ...(e.rank === 1 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{e.rank}</span>
            <Avatar user={e} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...st.taskLabel, fontSize: 18, display: "inline-flex", alignItems: "center", gap: 7 }}>{e.name}{e.is_me ? <Tag tone="neutral" size="xs">TY</Tag> : null}</div>
              {/* #7 — tenký bar úspešnosti (podiel splnených naplánovaných úloh) */}
              {e.percent != null && (
                <div style={{ height: 7, borderRadius: 99, background: "var(--accent-tint)", marginTop: 8, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${e.percent}%`, background: "var(--accent-grad)" }} />
                </div>
              )}
              {/* #7 — zložky metriky: úspešnosť · splnené/naplánované · séria · body */}
              <div style={{ ...st.taskDesc, marginTop: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ fontWeight: 700 }}>{e.percent != null ? `${e.percent} %` : "—"}</span>
                <span>{e.done}/{e.planned} splnené</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Icon name="streak" size={13} color="var(--ember)" />{e.streak}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Icon name="points" size={13} color="var(--honey)" />{e.points}</span>
              </div>
            </div>
            {/* pravý stĺpec: skóre hore, ikona správy dole (stretch na výšku karty) */}
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", alignSelf: "stretch", flexShrink: 0, gap: 10 }}>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 22, lineHeight: 1, color: "var(--accent)" }}>{e.score}</span>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--muted)", marginTop: 3 }}>skóre</span>
              </span>
              {onMessage && !e.is_me && (
                <button onClick={(ev) => { ev.stopPropagation(); onMessage(e); }}
                  style={{ ...st.iconBtn, background: "var(--surface)", borderRadius: 13, padding: 10 }} title="Napísať správu">
                  <Icon name="chat" size={17} color="currentColor" />
                </button>
              )}
            </span>
          </div>
          );
        })}
      </div>
    </>
  );
}
// embedded — beží vnútri Priateľ-hubu (SocialTab): vlastný nadpis + prepínač skryté,
// aktívnu pod-sekciu (aktivita | ludia) diktuje forcedSub.
export function FriendsTab({ reload, mode, me, createWhat, createKey, focusSub, onFocusHandled, embedded = false, forcedSub }) {
  const calMode = mode === "calendar";
  const [data, setData] = useState(null);
  const [convos, setConvos] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatConvo, setChatConvo] = useState(null);  // → ChatView
  const [creating, setCreating] = useState(false);   // → NewChat (group)
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [inviteDismissed, setInviteDismissed] = useState(false);  // F4: hide the empty-search invite
  const [addFriendOpen, setAddFriendOpen] = useState(false);  // pridať priateľa → modálne okno
  useEffect(() => {
    if (createKey && createWhat === "friend") {
      setSubView("ludia"); setAddFriendOpen(true);
      setTimeout(() => { searchRef.current?.focus(); }, 120);
    }
  }, [createKey]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [subView, setSubView] = useState("ludia");  // F2: Aktivita | Ľudia (Správy moved to its own tab)
  const activeSub = embedded ? (forcedSub || "ludia") : subView;  // v32.17 — v hube diktuje sub rodič
  const [newChat, setNewChat] = useState(false);     // v10: Nový chat trigger (lifted from ChatTab)
  useEffect(() => { window.scrollTo(0, 0); }, [subView]);  // A4
  // v18 §4 — a friend-request notification focuses the Ľudia sub-view
  useEffect(() => { if (focusSub) { setSubView(focusSub); onFocusHandled && onFocusHandled(); } }, [focusSub]);  // eslint-disable-line react-hooks/exhaustive-deps
  const searchTimer = useRef(null);
  const searchRef = useRef(null);
  const load = useCallback(async () => { setData(await api("/api/friends")); }, []);
  const loadConvos = useCallback(async () => {
    if (calMode) return;
    try { setConvos((await api("/api/conversations")).conversations || []); } catch (e) { /* */ }
  }, [calMode]);
  useEffect(() => { load(); loadConvos(); }, [load, loadConvos]);

  // mapovanie priateľ → 1:1 konverzácia (na počet neprečítaných v zozname priateľov)
  const oneToOne = {};
  for (const c of convos) if (!c.is_group && c.members && c.members[0]) oneToOne[c.members[0].id] = c;

  function onSearch(v) {
    setQuery(v);
    setInviteDismissed(false);  // F4: a fresh search re-shows the invite block
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.trim().length < 2) { setResults(null); return; }
    searchTimer.current = setTimeout(async () => {
      try { setResults((await api("/api/users/search?q=" + encodeURIComponent(v.trim()))).users); }
      catch (e) { setResults([]); }
    }, 300);
  }
  async function sendRequest(uid) {
    try { await api("/api/friends/request", { method: "POST", body: { user_id: uid } }); }
    catch (e) { setErr(e.message); }
    onSearch(query); load(); reload();
  }
  // v22 — vzťahový štítok s novými ikonami (namiesto ⏳/📨 emoji)
  const relNode = (rel) => rel === "friend" ? "Priateľ"
    : rel === "outgoing" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="hourglass" size={12} color="currentColor" /> Čaká</span>
      : rel === "incoming" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="mail" size={12} color="currentColor" /> Pozval ťa</span>
        : "";

  async function invite() {
    const em = query.trim();  // pozvánka používa rovnaký input ako hľadanie (žiadne ďalšie pole)
    if (!em) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      const r = await api("/api/friends/invite", { method: "POST", body: { email: em } });
      const m = r.accepted ? "Priateľstvo potvrdené! 🎉" : r.pending ? "Pozvánka odoslaná — keď sa zaregistruje rovnakým e-mailom, stanete sa priateľmi. ✉️" : "Pozvánka odoslaná. ✉️";
      setMsg(m); setInviteDismissed(true);          // F4: collapse the block, show transient confirmation
      setAddFriendOpen(false); setQuery(""); setResults(null);  // zatvor modál, potvrdenie sa ukáže pod tlačidlom
      setTimeout(() => setMsg(""), 6000);
      load(); reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  // v-agent-b §2 — guard against a double-tap firing two accept/decline requests (the backend
  // is now idempotent too, but this avoids the redundant round-trip + any UI flicker from it).
  const [respondingFid, setRespondingFid] = useState(() => new Set());
  async function accept(fid) {
    if (respondingFid.has(fid)) return;
    setRespondingFid((s) => new Set(s).add(fid));
    try { await api("/api/friends/" + fid + "/accept", { method: "POST" }); load(); reload(); }
    finally { setRespondingFid((s) => { const n = new Set(s); n.delete(fid); return n; }); }
  }
  async function decline(fid) {
    if (respondingFid.has(fid)) return;
    setRespondingFid((s) => new Set(s).add(fid));
    try { await api("/api/friends/" + fid + "/decline", { method: "POST" }); load(); reload(); }
    finally { setRespondingFid((s) => { const n = new Set(s); n.delete(fid); return n; }); }
  }
  async function remove(fid) { if (!confirm("Odobrať priateľa?")) return; await api("/api/friends/" + fid, { method: "DELETE" }); load(); reload(); }
  async function openChat(f) {
    try {
      const r = await api("/api/conversations", { method: "POST", body: { member_ids: [f.friend_id] } });
      setChatConvo({ id: r.id, title: f.name, is_group: false, members: [{ id: f.friend_id, name: f.name, avatar: f.avatar }] });
    } catch (e) { setErr(e.message); }
  }
  // F8/F9 — otvor globálny verejný profil; akcie (správa/štuchnutie/výzva/… alebo „Pridať priateľa")
  // sú zabudované priamo v ňom a riadia sa údajmi profilu (rel/is_me) — tu stačí uid.
  // v33h — scroll navrch až PO odomknutí body-overflow (ChatView ho pri unmounte obnoví); inak
  // scrollTo(0,0) bežalo počas zamknutého scrollu a Správy ostali odscrollované.
  if (chatConvo) return <ChatView convo={chatConvo} me={me} onBack={() => { setChatConvo(null); loadConvos(); reload(); requestAnimationFrame(() => window.scrollTo(0, 0)); }} />;
  if (creating) return <NewChat onCancel={() => setCreating(false)}
    onCreated={(id) => { setCreating(false); loadConvos(); setChatConvo({ id, title: "Skupina", is_group: true, members: [] }); reload(); }} />;
  if (!data) return <div style={{ display: "flex", justifyContent: "center", padding: 30 }}><div className="spin" /></div>;

  return (
    <>
      {/* F1 — Priatelia heading (Dnes size); Aktivita/Ľudia switch on its own row below, right-aligned (Dnes style).
          v32.17 — v embedded hube nadpis + prepínač vlastní rodič (SocialTab), tu sú skryté. */}
      {!embedded && (<>
        <div style={{ ...st.heroHead }}>
          <h1 style={{ ...st.heroTitle, margin: 0 }}>Priatelia.</h1>
        </div>
        {!(me && me.lite_mode) && (
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "6px 2px 12px" }}>
            <Segmented options={[{ value: "aktivita", label: "Aktivita" }, { value: "ludia", label: "Ľudia" }]} value={subView === "aktivita" ? "aktivita" : "ludia"} onChange={setSubView} />
          </div>
        )}
      </>)}

      {/* N1 — úvodné vysvetlenie sekcie Aktivita (Lite aj non-Lite, rovnaký štýl ako pri Ľudia). */}
      {activeSub === "aktivita" && (
        <div style={{ ...st.infoNote, marginTop: 10, marginBottom: 4 }}>Tu vidíš, čo tvoji priatelia dnes dokázali — splnené návyky, série a výzvy.</div>
      )}

      {/* v32.21 — „Pridať priateľa" navrch celej sekcie Priatelia (nad rebríčkom) */}
      {activeSub === "ludia" && (<>
        <Button variant="outline" block data-tour="invite" onClick={() => setAddFriendOpen(true)} style={{ marginTop: 4 }}>
          <Icon name="addfriend" size={17} color="currentColor" />Pridať priateľa
        </Button>
        <div style={{ ...st.infoNote, marginTop: 10, marginBottom: 4 }}>Pridaj si nových priateľov alebo pozvi niekoho, komu by sa Buzerlístok určite páčil.</div>
        {msg && <div style={{ ...st.infoNote, marginTop: 8 }}>{msg}</div>}
      </>)}

      {/* v32.21 — rebríček žije v Prehľade; v Priateľoch je späť klasický zoznam priateľov */}
      {activeSub === "ludia" && (<>
        {addFriendOpen && (
          <Modal center title="Pridať priateľa" icon="addfriend" onClose={() => { setAddFriendOpen(false); setQuery(""); setResults(null); setErr(""); }}>
            <div style={{ position: "relative" }}>
              <BuzerIcon name="search" size={16} color="var(--neutral-text-subtle)" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              {/* v32 — bez autoFocus: modal sa otvorí CENTROVANÝ (autofocus otváral klávesnicu
                  a keyboard-pin ho hneď roztiahol na celú obrazovku) */}
              <input ref={searchRef} className="field" style={{ ...st.field, width: "100%", paddingLeft: 38 }}
                placeholder="Hľadať používateľov (meno alebo e-mail)"
                value={query} onChange={(e) => onSearch(e.target.value)} />
            </div>
            {results && (results.length === 0 ? (!inviteDismissed && (
              // F4/F7 — nobody found → dismissible invite-by-email block
              <div style={{ ...st.infoNote, marginTop: 10, position: "relative" }}>
                <button type="button" onClick={() => setInviteDismissed(true)} aria-label="Skryť" title="Skryť"
                  style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>✕</button>
                <div style={{ fontWeight: 700, marginBottom: 4, paddingRight: 18 }}>Nikto taký tu zatiaľ nie je</div>
                <div style={{ ...st.taskDesc, marginBottom: 8 }}>Napíš jeho e-mail vyššie a pošli pozvánku — keď sa zaregistruje rovnakým e-mailom, automaticky sa stanete priateľmi.</div>
                <button type="button" onClick={invite} disabled={busy || !/.+@.+\..+/.test(query.trim())}
                  style={{ ...st.primaryBtn, width: "100%", opacity: (busy || !/.+@.+\..+/.test(query.trim())) ? 0.5 : 1 }}>
                  {busy ? "…" : "Pozvať e-mailom"}
                </button>
              </div>
            )) : (
              <div style={{ ...st.taskList, marginTop: 10 }}>
                {results.map((u) => (
                  <div key={u.id} className="task" style={st.task}>
                    <span style={{ paddingLeft: 2 }}><Avatar user={u} size={34} onClick={() => openPublicProfile(u.id)} /></span>
                    <button onClick={() => openPublicProfile(u.id)} style={st.taskMain}>
                      <span style={st.taskText}><span style={st.taskLabel}>{u.name}</span>
                        <span style={st.taskDesc}>{u.rel !== "none" ? relNode(u.rel) : "klikni a zobraz profil"}</span></span>
                    </button>
                    {/* F9 — view profile (whole row) + add */}
                    {u.rel === "none" && <button onClick={() => sendRequest(u.id)} style={{ ...st.doneBtn, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="add" size={13} color="currentColor" /> Pridať</button>}
                  </div>
                ))}
              </div>
            ))}
            {err && <div style={st.err}>{err}</div>}
          </Modal>
        )}

        {data.incoming.length > 0 && (
          <>
            <div style={{ ...st.progressHead, marginTop: 20 }}><span className="bz-subhead">📨 Čakajúce pozvánky</span></div>
            <div style={st.taskList}>
              {data.incoming.map((f) => (
                <div key={f.fid} data-friendid={f.user_id} className="task" style={st.task}>
                  <span style={{ paddingLeft: 2 }}><Avatar user={f} size={34} onClick={() => openPublicProfile(f.user_id)} /></span>
                  <span style={st.taskText}>
                    <span style={st.taskLabel}>{f.name}</span>
                    <span style={st.taskDesc}>Chce ťa pridať za priateľa</span>
                  </span>
                  <button onClick={() => accept(f.fid)} disabled={respondingFid.has(f.fid)} style={st.doneBtn}>Prijať</button>
                  <button onClick={() => decline(f.fid)} disabled={respondingFid.has(f.fid)} style={st.del} title="Odmietnuť">×</button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* zoznam priateľov (klik na listu → verejný profil; ikona = správa; × = odobrať) */}
        <div style={{ ...st.taskList, marginTop: 18 }}>
          {data.friends.map((f) => {
            const conv = oneToOne[f.friend_id];
            const unread = conv ? conv.unread : 0;
            return (
              <div key={f.fid} data-friendid={f.friend_id} className="task" style={st.task}>
                <span style={{ paddingLeft: 2 }}><Avatar user={f} size={34} onClick={() => openPublicProfile(f.friend_id)} /></span>
                <button onClick={() => openPublicProfile(f.friend_id)} style={st.taskMain}>
                  <span style={st.taskText}>
                    <span style={st.taskLabel}>{f.name}</span>
                    <span style={st.taskDesc}>Klikni a zobraz profil</span>
                  </span>
                  <span style={st.taskRight}>
                    {/* A2 — iba samotný unreadDot otvorí chat (stopPropagation, nech neprebublá do
                        riadku → ten ostáva na verejný profil). */}
                    {unread > 0 && <span role="button" tabIndex={0} title="Otvoriť správu"
                      onClick={(e) => { e.stopPropagation(); openChat(f); }}
                      style={{ ...st.unreadDot, cursor: "pointer" }}>{unread}</span>}
                    <span style={st.taskPts}>›</span>
                  </span>
                </button>
                <button onClick={() => openChat(f)} style={st.iconBtn} title="Napísať správu"><Icon name="chat" size={16} color="currentColor" /></button>
                <button onClick={() => remove(f.fid)} style={st.del} title="Odobrať">×</button>
              </div>
            );
          })}
        </div>
        {data.friends.length === 0 && <div style={st.empty}>Zatiaľ žiadni priatelia — pozvi niekoho</div>}

        {data.outgoing.length > 0 && (
          <>
            <div style={{ ...st.progressHead, marginTop: 20 }}><span className="bz-subhead"><TI name="reminder" color="var(--muted)" />Odoslané pozvánky</span></div>
            <div style={st.taskList}>
              {data.outgoing.map((f) => (
                <div key={f.fid} className="task" style={{ ...st.task, opacity: 0.8 }}>
                  <span style={{ paddingLeft: 2 }}><Avatar user={f} size={34} onClick={() => openPublicProfile(f.user_id)} /></span>
                  <span style={st.taskText}>
                    <span style={st.taskLabel}>{f.name}</span>
                    <span style={st.taskDesc}>Čaká na potvrdenie</span>
                  </span>
                  <button onClick={() => remove(f.fid)} style={st.del} title="Zrušiť">×</button>
                </div>
              ))}
            </div>
          </>
        )}
      </>)}

    </>
  );
}
// ─────────────────────────────────────────────────────────────
//  Chat — konverzácie + vlákno (živé cez polling)
// ─────────────────────────────────────────────────────────────
export function NewChat({ onCreated, onCancel }) {
  const [friends, setFriends] = useState([]);
  const [sel, setSel] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {}); }, []);
  function toggle(id) { setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  async function create(e) {
    e.preventDefault();
    if (!sel.length) { setErr("Vyber aspoň jedného priateľa."); return; }
    setBusy(true);
    try {
      const r = await api("/api/conversations", { method: "POST",
        body: { member_ids: sel, name: sel.length > 1 ? name : undefined } });
      onCreated(r.id);
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <form onSubmit={create} style={{ ...st.card, background: "var(--surface)", marginTop: 4 }}>
      <div style={st.fieldHint}>Vyber priateľov:</div>
      {friends.length === 0 ? <div style={st.taskDesc}>Zatiaľ žiadni priatelia.</div> : (
        <><FriendPickerHint />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {friends.map((f) => {
            const on = sel.includes(f.friend_id);
            return <FriendChip key={f.friend_id} friend={f} selected={on} onToggle={toggle}
              chipStyle={{ ...st.chip, flex: "none" }} chipOnStyle={st.chipOn} />;
          })}
        </div></>
      )}
      {sel.length > 1 && <input className="field" style={st.field} placeholder="Názov skupiny"
        value={name} onChange={(e) => setName(e.target.value)} />}
      {err && <div style={st.err}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={onCancel} style={st.cancelBtn}>Zrušiť</button>
        <button type="submit" disabled={busy} style={{ ...st.primaryBtn, flex: 1 }}>{busy ? "…" : "Začať chat"}</button>
      </div>
    </form>
  );
}
export function GroupAddSheet({ cid, existingIds, onClose, onAdded }) {
  useModalBackClose(onClose);
  const [friends, setFriends] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => setFriends([])); }, []);
  const ex = new Set(existingIds || []);
  const avail = (friends || []).filter((f) => !ex.has(f.friend_id));
  function toggle(id) { setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  async function add() {
    if (!sel.length) return;
    setBusy(true); setErr("");
    try { await api("/api/conversations/" + cid + "/members", { method: "POST", body: { member_ids: sel } }); onAdded(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <div style={st.sheetWrap} onClick={onClose}>
      <div style={st.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={st.sheetHead}>
          <span style={st.sheetTitle}><TI name="friends" color="var(--plum)" />Pridať do skupiny</span>
          <button onClick={onClose} style={st.sheetClose} aria-label="Zavrieť">✕</button>
        </div>
        {friends === null ? <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><div className="spin" /></div>
          : avail.length === 0 ? <div style={st.taskDesc}>Všetci tvoji priatelia už sú v skupine.</div>
          : <><FriendPickerHint />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {avail.map((f) => (
                <FriendChip key={f.friend_id} friend={f} selected={sel.includes(f.friend_id)}
                  onToggle={toggle} chipStyle={{ ...st.chip, flex: "none" }} chipOnStyle={st.chipOn} />
              ))}
            </div></>}
        {err && <div style={st.err}>{err}</div>}
        <button onClick={add} disabled={busy || !sel.length} style={{ ...st.primaryBtn, width: "100%", marginTop: 12 }}>{busy ? "…" : "Pridať"}</button>
      </div>
    </div>
  );
}
export function ChatView({ convo, me, onBack, zIndex }) {
  useModalBackClose(onBack);  // v33h — hardvérové/prehliadačové „Späť" zavrie chat → Správy (nie odchod z appky)
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [msgs, setMsgs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [pokeMsg, setPokeMsg] = useState(null);  // v32.18 — otvorený detail poke správy
  const [text, setText] = useState("");
  const lastId = useRef(0);
  const boxRef = useRef(null);
  // size the chat to the *visible* viewport so the keyboard never covers the input
  const [vp, setVp] = useState(() => ({
    h: (window.visualViewport && window.visualViewport.height) || window.innerHeight, top: 0,
  }));
  const scrollToBottom = useCallback(() => {
    const b = boxRef.current;
    if (b) b.scrollTop = b.scrollHeight;
  }, []);
  const polling = useRef(false);
  const poll = useCallback(async () => {
    if (polling.current) return;  // C2: don't let overlapping polls race
    polling.current = true;
    try {
      const d = await api("/api/conversations/" + convo.id + "/messages?after=" + lastId.current);
      if (d.messages && d.messages.length) {
        lastId.current = Math.max(lastId.current, ...d.messages.map((m) => m.id));
        // C2: always append + dedupe by id — never clear/replace (messages can't vanish)
        setMsgs((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const add = d.messages.filter((m) => !seen.has(m.id));
          return add.length ? [...prev, ...add] : prev;
        });
      }
    } catch (e) { /* transient */ } finally { polling.current = false; }
  }, [convo.id]);
  useEffect(() => { lastId.current = 0; setMsgs([]); poll(); }, [poll]);
  usePoll(poll, 3000, true);
  useEffect(() => { scrollToBottom(); }, [msgs, vp, scrollToBottom]);
  // track the visual viewport (shrinks/offsets when the mobile keyboard opens)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => { setVp({ h: vv.height, top: vv.offsetTop || 0 }); setTimeout(scrollToBottom, 50); };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, [scrollToBottom]);
  // lock the page behind the full-screen chat overlay
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  async function send(e) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setText("");
    try {
      const r = await api("/api/conversations/" + convo.id + "/messages", { method: "POST", body: { body } });
      lastId.current = Math.max(lastId.current, r.id);
      // A1 — idempotent by id: the 3s poll can fetch this same message (id=r.id) before the POST
      // resolves and append it first; skip our optimistic insert if it's already there (same dedup
      // as poll()) so the sent message never briefly shows twice.
      setMsgs((prev) => prev.some((m) => m.id === r.id) ? prev
        : [...prev, { id: r.id, mine: true, sender: me.name, avatar: me.avatar, body, at: r.at, sender_id: me.id }]);
    } catch (e) { setText(body); }
  }
  const other = !convo.is_group && convo.members && convo.members[0];
  return (
    <div style={{ ...st.chatOverlay, top: vp.top, height: vp.h, ...(zIndex != null ? { zIndex } : {}) }}>
      <div style={{ ...st.topbar, marginBottom: 8, flexShrink: 0 }}>
        <button onClick={onBack} style={st.logout}>‹ Správy</button>
        <span style={{ ...st.hello, display: "flex", alignItems: "center", gap: 8, cursor: other ? "pointer" : "default" }}
          onClick={() => other && openPublicProfile(other.id)}>
          {other && <Avatar user={other} size={26} />}{convo.title}
        </span>
        {convo.is_group
          ? <button onClick={() => setAdding(true)} style={st.logout} title="Pridať do skupiny">＋ Pridať</button>
          : <span style={{ width: 52 }} />}
      </div>
      {adding && <GroupAddSheet cid={convo.id} existingIds={(convo.members || []).map((m) => m.id)}
        onClose={() => setAdding(false)} onAdded={() => setAdding(false)} />}
      <div ref={boxRef} style={st.msgList}>
        {msgs.length === 0 && <div style={st.empty}>Zatiaľ žiadne správy — napíš prvú.</div>}
        {msgs.map((m, i) => {
          // deň-delič: keď sa kalendárny deň správy líši od predošlej, vlož centrované záhlavie dňa
          const dayKey = (m.at || "").slice(0, 10);
          const prevKey = i > 0 ? (msgs[i - 1].at || "").slice(0, 10) : "";
          const dayEl = dayKey && dayKey !== prevKey
            ? <div style={st.chatDayDivider}>{chatDayHeading(dayKey)}</div> : null;
          const tm = chatTime(m.at);
          // v32.18 — šťuchnutie (poke) = zvláštna klikacia bublina; klik otvorí detail + odpoveď
          if (m.kind === "poke") {
            return (
              <Fragment key={m.id}>
              {dayEl}
              <div style={{ ...st.bubbleRowChat, justifyContent: m.mine ? "flex-end" : "flex-start" }}>
                <button type="button" onClick={() => setPokeMsg(m)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 9, maxWidth: "82%", padding: "9px 13px", borderRadius: 16, border: "1px solid var(--ember)", background: "var(--ember-soft)", color: "var(--text)", cursor: "pointer", textAlign: "left" }}>
                  <span className="shake" style={{ display: "inline-flex", flexShrink: 0 }}><Icon name="buzerak" size={20} color="var(--ember)" /></span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 10.5, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ember)", fontFamily: "var(--ff-mono)" }}>{m.mine ? "Štuchol/a si" : "Šťuchnutie"}</span>
                    <span style={{ display: "block", fontSize: 14.5, fontWeight: 600 }}>{m.body}</span>
                    {tm && <span style={{ ...st.bubbleTime, display: "block" }}>{tm}</span>}
                  </span>
                </button>
              </div>
              </Fragment>
            );
          }
          return (
          <Fragment key={m.id}>
          {dayEl}
          <div style={{ ...st.bubbleRowChat, justifyContent: m.mine ? "flex-end" : "flex-start" }}>
            <div style={{ ...st.bubble, ...(m.is_ai ? st.bubbleAi : (m.mine ? st.bubbleMine : st.bubbleTheirs)) }}>
              {m.is_ai ? <div style={{ ...st.bubbleMeta, color: "var(--ai-text)", display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="ai" size={12} /> {m.sender}</div>
                : (!m.mine && convo.is_group && <div style={st.bubbleMeta}>{m.sender}</div>)}
              {/* v32 — zdieľané štatistiky: dlaždice nad textom správy (viac ich príde v jednej správe) */}
              {m.kind === "stat" && m.payload && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "2px 0 7px" }}>
                  {(m.payload.stats || [m.payload]).map((p, i) => (
                    <StatMiniTile key={i} stat={p} size="sm" style={{ minWidth: 180 }} />
                  ))}
                </div>
              )}
              {m.body}
              {tm && <div style={st.bubbleTime}>{tm}</div>}
            </div>
          </div>
          </Fragment>
          );
        })}
      </div>
      {pokeMsg && <PokeModal text={pokeMsg.body} fromName={pokeMsg.mine ? null : pokeMsg.sender}
        fromId={pokeMsg.mine ? null : pokeMsg.sender_id} canReply={!pokeMsg.mine}
        onClose={() => setPokeMsg(null)} onReplied={() => poll()} />}
      <form onSubmit={send} style={{ ...st.chatInput, flexShrink: 0 }}>
        <input className="field" style={{ ...st.field, flex: 1 }} placeholder="Napíš správu…"
          value={text} onChange={(e) => setText(e.target.value)}
          onFocus={() => setTimeout(scrollToBottom, 350)} autoFocus />
        <button type="submit" style={{ ...st.primaryBtn, width: 46, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-label="Odoslať"><Icon name="send" size={20} color="currentColor" /></button>
      </form>
    </div>
  );
}
// Skupinový chat výzvy/súťaže. Ikona chatu sa vloží do hlavičky detailu výzvy (vedľa ×). Klik:
// ak už som v chate → rovno otvorí vlákno; inak sa opýta, či sa chcem zapojiť, a po potvrdení
// ma pridá + otvorí. Chat je potom dostupný aj v Priatelia → Správy. Názov chatu = názov výzvy.
// Renderuje samo aj potvrdzovací dialóg aj celoobrazovkový ChatView (nad rodičovským modalom).
export function ChallengeChatButton({ cid, name, me, style }) {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);      // potvrdenie „zapojiť sa do chatu?"
  const [chat, setChat] = useState(null);      // otvorené vlákno → ChatView
  const [info, setInfo] = useState(null);      // { eligible, joined, unread, conversation_id }
  const [err, setErr] = useState("");
  // stav chatu (vrátane počtu neprečítaných) načítaj po otvorení detailu a ľahko poll-uj, nech
  // sa bodka objaví hneď, ako príde nová správa. Kým je chat otvorený, poll pauzujeme.
  const loadInfo = useCallback(async () => {
    if (!session.premium) return;
    try { setInfo(await api("/api/challenges/" + cid + "/chat")); } catch (e) { /* transient */ }
  }, [cid]);
  useEffect(() => { loadInfo(); }, [loadInfo]);
  usePoll(loadInfo, 8000, !chat && !ask);
  if (!session.premium) return null;  // chat je Premium (rovnako ako Správy)
  const hasUnread = !!(info && info.joined && info.unread > 0);

  async function onClick() {
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      const r = info && info.joined ? info : await api("/api/challenges/" + cid + "/chat");
      if (r.joined && r.conversation_id) {
        setChat({ id: r.conversation_id, title: r.name || name, is_group: true, members: [] });
      } else if (r.eligible) {
        setAsk(true);
      } else {
        setErr("Do skupinového chatu sa môžu zapojiť len účastníci výzvy.");
      }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  async function join() {
    setBusy(true); setErr("");
    try {
      const r = await api("/api/challenges/" + cid + "/chat/join", { method: "POST" });
      setAsk(false);
      setChat({ id: r.id, title: r.name || name, is_group: true, members: [] });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <>
      <IconButton variant="ghost" size="sm" onClick={onClick} disabled={busy}
        aria-label={hasUnread ? "Skupinový chat — nové správy" : "Skupinový chat"} title="Skupinový chat"
        style={{ position: "relative", ...(style || {}) }}>
        <Icon name="chat" size={18} color="currentColor" />
        {hasUnread && <span style={st.topDot} />}
      </IconButton>
      {ask && (
        <Modal center title="Skupinový chat výzvy" icon="chat" maxWidth={380} onClose={() => setAsk(false)}>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>
            Chceš sa zapojiť do skupinového chatu <b>{name}</b>? Píšte si s ostatnými účastníkmi —
            chat nájdeš aj v <b>Priatelia → Správy</b>.
          </div>
          {err && <div style={{ ...st.err, marginTop: 10 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setAsk(false)}>Zrušiť</Button>
            <Button variant="primary" block disabled={busy} onClick={join}>
              <Icon name="chat" size={16} color="currentColor" />{busy ? "…" : "Zapojiť sa"}
            </Button>
          </div>
        </Modal>
      )}
      {/* portal do <body> + vysoký zIndex, nech ChatView nie je orezaný/pod rodičovským sheetom (scrim = 300) */}
      {chat && createPortal(<ChatView convo={chat} me={me} zIndex={520} onBack={() => { setChat(null); loadInfo(); }} />, document.body)}
      {err && !ask && !chat && (
        <Modal center title="Chat" icon="chat" maxWidth={340} onClose={() => setErr("")}>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>{err}</div>
          <Button variant="primary" block style={{ marginTop: 14 }} onClick={() => setErr("")}>OK</Button>
        </Modal>
      )}
    </>
  );
}
export function ChatTab({ reload, me, onConvos, creating: creatingProp, onCreatingChange, focus, onFocusHandled }) {
  const [convos, setConvos] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState("all");  // all | priatelia (bežné) | vyzvy (chaty výziev)
  // v26.2 §1 — deep-link: open a specific thread from a group-add notification
  useEffect(() => { if (focus != null) { setActiveId(Number(focus)); onFocusHandled && onFocusHandled(); } }, [focus]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [creatingState, setCreatingState] = useState(false);
  const creating = creatingProp !== undefined ? creatingProp : creatingState;  // v10 — Nový chat lifted to FriendsTab
  const setCreating = onCreatingChange || setCreatingState;
  const loadConvos = useCallback(async () => {
    const cs = (await api("/api/conversations")).conversations;
    setConvos(cs);
    onConvos && onConvos(cs);  // keep the parent's Správy-pill unread badge in sync
  }, [onConvos]);
  useEffect(() => { loadConvos(); }, [loadConvos]);
  // light polling of the list so unread/last-message stay fresh while browsing
  usePoll(loadConvos, 6000, activeId === null && !creating);

  if (activeId !== null) {
    const convo = (convos || []).find((c) => c.id === activeId) || { id: activeId, title: "Chat", members: [], is_group: false };
    return <ChatView convo={convo} me={me} onBack={() => { setActiveId(null); loadConvos(); reload(); window.scrollTo(0, 0); }} />;
  }
  if (creating) return (
    <NewChat onCancel={() => setCreating(false)}
      onCreated={(id) => { setCreating(false); loadConvos(); setActiveId(id); reload(); }} />
  );
  if (!convos) return <div style={{ display: "flex", justifyContent: "center", padding: 30 }}><div className="spin" /></div>;
  // filter: Všetky · Priatelia (bežné 1:1 + skupiny) · Výzvy (skupinové chaty výziev/súťaží)
  const hasChallengeChats = convos.some((c) => c.is_challenge);
  const shown = filter === "priatelia" ? convos.filter((c) => !c.is_challenge)
    : filter === "vyzvy" ? convos.filter((c) => c.is_challenge)
      : convos;
  return (
    <>
      {/* v20 §16 — inline „Nová správa" odstránené; nová správa sa zakladá cez univerzálne „+" (§24) */}
      {/* filter sa objaví, až keď existuje aspoň jeden chat výzvy (inak je zbytočný) */}
      {hasChallengeChats && (
        <div style={{ marginBottom: 12 }}>
          <Segmented options={[{ value: "all", label: "Všetky" }, { value: "priatelia", label: "Priatelia" }, { value: "vyzvy", label: "Výzvy" }]}
            value={filter} onChange={setFilter} />
        </div>
      )}
      {convos.length === 0 && <div style={st.empty}><BuzerIcon name="chat" size={18} color="var(--muted)" style={{ verticalAlign: "-3px", marginRight: 6 }} />Žiadne konverzácie — začni novú správu</div>}
      {convos.length > 0 && shown.length === 0 && <div style={st.empty}>{filter === "vyzvy" ? "Žiadne chaty výziev." : "Žiadne konverzácie v tomto filtri."}</div>}
      {shown.map((c) => (
        <button key={c.id} onClick={() => setActiveId(c.id)} style={st.convRow}>
          {c.is_challenge
            ? <div style={{ width: 36, display: "flex", justifyContent: "center" }}><BuzerIcon name="flag" size={22} color="var(--brand-primary)" /></div>
            : c.is_group
              ? <div style={{ width: 36, display: "flex", justifyContent: "center" }}><BuzerIcon name="people" size={24} color="var(--text2)" /></div>
              : <Avatar user={c.members[0]} size={36} />}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ ...st.taskLabel, fontSize: 15, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{c.title}</span>
              {c.is_challenge && <Tag tone="brand" size="xs">Výzva</Tag>}
            </span>
            <span style={{ ...st.taskDesc, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
              {c.last ? (c.last.mine ? "Ty: " : "") + c.last.body : "—"}
            </span>
          </span>
          {c.unread > 0 && <span style={st.unreadDot}>{c.unread}</span>}
        </button>
      ))}
    </>
  );
}
// v2 — Správy as its own menu item (moved out of Priatelia)
export function SpravyTab({ reload, me, createKey, createWhat, focus, onFocusHandled, embedded = false }) {
  const [newChat, setNewChat] = useState(false);
  useEffect(() => { if (createKey && createWhat === "message") setNewChat(true); }, [createKey]);
  if (!session.premium) return <ProUpsell title="Správy priateľom" />;
  return (
    <>
      {!embedded && <div style={{ ...st.heroHead }}><h1 style={{ ...st.heroTitle, margin: 0 }}>Správy.</h1></div>}
      {!newChat && (
        <Button variant="outline" block onClick={() => setNewChat(true)} style={{ margin: "10px 0 4px" }}>
          <BuzerIcon name="add" size={17} color="currentColor" strokeWidth={2.2} />Nová správa
        </Button>
      )}
      <div style={{ marginTop: 8 }}>
        <ChatTab reload={reload} me={me} creating={newChat} onCreatingChange={setNewChat} focus={focus} onFocusHandled={onFocusHandled} />
      </div>
    </>
  );
}

export function PublicProfile({ uid }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [poking, setPoking] = useState(false);  // PR8: Šťuchnúť sheet (friends only)
  const load = useCallback(async () => {
    try { setP(await api("/api/users/" + uid + "/profile")); }
    catch (e) { setErr(e.message); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);
  // v20 — aplikuj uloženú tému prezerajúceho, nech farby (vrátane Community) sedia s témou
  useEffect(() => { try { applyAppTheme(getAppTheme()); } catch { /* */ } }, []);

  async function react(emoji) {
    try { await api("/api/users/" + uid + "/status/react", { method: "POST", body: { emoji } }); load(); }
    catch (e) { /* */ }
  }

  // Item 2 — Lite profil dostane sivú „grey tier" kartu (rovnaké elementy, len tlmený sivý povrch);
  //          ostatní podľa levelu; bez levelu/tieru = žiadny obal (undefined).
  const pcard = p ? (p.lite ? liteCardStyle() : tierCardStyle(profileTier(p))) : null;

  return (
    <div style={st.page}>
      <div style={{ ...st.container, maxWidth: 440 }}>
        <div style={{ ...st.topbar, marginBottom: 14 }}>
          {/* v17 — back returns to wherever the profile was opened from (history), else app home */}
          <button onClick={() => { if (window.history.length > 1) window.history.back(); else window.location.href = "/"; }} style={st.logout}>‹ Späť</button>
        </div>
        {err && <div style={st.err}>{err}</div>}
        {!p ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><div className="spin" /></div> : (
          <>
            {/* v32 — telo profilu je zdieľané s ProfileModal (components/ui/ProfileBody).
                v33h — CELÝ panel prefarbený podľa tieru (Avatar.tierCardStyle); Lite/bez levelu → bez obalu. */}
            <div style={pcard ? { ...pcard, borderRadius: "var(--radius-lg)", padding: "8px 14px 16px", overflow: "hidden" } : undefined}>
              <ProfileBody p={p} onReact={react}
                heroAction={!p.is_me && p.rel === "friend" && (
                  <button onClick={() => setPoking(true)} style={{ ...st.primaryBtn, marginTop: 14, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}><BuzerIcon name="buzerak" size={16} color="var(--on-ember)" /> Šťuchnúť</button>
                )} />
            </div>
            {poking && <BuzerakSheet target={{ id: p.id, name: p.name }} onClose={() => setPoking(false)} onSent={() => setPoking(false)} />}
          </>
        )}
      </div>
    </div>
  );
}

// v32.19 — Social hub: pilulkový prepínač (ako v Prehľade) — Správy · Priatelia · Zapoj sa.
// „Priatelia" zlučuje Ľudí + rebríček (Aktivita integrovaná, žiadny samostatný button).
// Nadpis „SOCIAL" je v hornej lište (App). Výzvy sú voľne dostupné aj bez Community.
export function SocialTab({ s, reload, me, createWhat, createKey, focusSub, onFocusHandled, chatFocus, onChatFocusHandled }) {
  const premium = session.premium;
  // v32.21 — pri vstupe na Social: ak už existujú správy → Správy, inak Priatelia
  const [sub, setSub] = useState(() => (s.has_conversations ? "spravy" : "priatelia"));
  // staré hodnoty focusSub (ludia/aktivita) mapuj na „priatelia"
  const norm = (v) => (v === "ludia" || v === "aktivita") ? "priatelia" : v;
  useEffect(() => { if (focusSub) { setSub(norm(focusSub)); onFocusHandled && onFocusHandled(); } }, [focusSub]);  // eslint-disable-line react-hooks/exhaustive-deps
  // „Pridať priateľa" (+ → priateľ) aj „Nová správa" prepnú na správnu pod-sekciu
  useEffect(() => { if (createKey && createWhat === "friend") setSub("priatelia"); if (createKey && createWhat === "message") setSub("spravy"); }, [createKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { window.scrollTo(0, 0); }, [sub]);
  // v33i (task #6) — „Zapoj sa" (výzvy) ostáva dostupné aj v Lite, doteraz sa skrývalo.
  const sections = [
    { key: "spravy", label: "Správy", icon: "chat" },
    { key: "priatelia", label: "Priatelia", icon: "people" },
    { key: "vyzvy", label: "Zapoj sa", icon: "flag" },
  ];
  return (
    <>
      <PrehladSwitch sections={sections} value={sub} onChange={setSub} />
      {sub === "vyzvy" ? <ChallengesTab reload={reload} embedded me={me} />
        : !premium ? <ProUpsell title="Priatelia, chat a zdieľané kalendáre" />
        : sub === "spravy" ? <SpravyTab embedded reload={reload} me={me} createKey={createKey} createWhat={createWhat} focus={chatFocus} onFocusHandled={onChatFocusHandled} />
        : <FriendsTab reload={reload} mode="chat" me={me} createWhat={createWhat} createKey={createKey} embedded forcedSub="ludia" />}
    </>
  );
}
export function BuzerakSheet({ target, onClose, onSent, zIndex }) {
  useScrollLock();
  useModalBackClose(onClose);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function send(text) {
    setBusy(true); setErr("");
    try { await api("/api/friends/" + target.id + "/buzerak", { method: "POST", body: { text } }); onSent(); }
    catch (e) { setErr(e.message); setBusy(false); }
  }
  // fix/nudge-keyboard v2 — SPODNE ukotvená plávajúca karta (nie vycentrovaná). Centrovanie spôsobovalo,
  // že pri otvorení klávesnice karta „vyletela" hore (vrch mimo obrazovky). Ukotvením k spodku (justify
  // flex-end) + paddingBottom = klávesnica + 12px karta sadne TESNE nad klávesnicu; textové pole tak
  // ostane pár px nad ňou a celá karta je viditeľná. Zaoblené všetky rohy → stále pôsobí ako modal.
  return (
    <div style={{ ...st.sheetWrap, justifyContent: "flex-end", alignItems: "center", padding: "0 12px", paddingBottom: "calc(var(--kb-inset, 0px) + 12px)", ...(zIndex != null ? { zIndex } : {}) }} onClick={onClose}>
      <div style={{ ...st.sheet, borderBottomLeftRadius: 18, borderBottomRightRadius: 18, maxWidth: 460, maxHeight: "calc(86dvh - var(--kb-inset, 0px))" }} onClick={(e) => e.stopPropagation()}>
        <div style={st.sheetHead}>
          <span style={st.sheetTitle}><span className="shake" style={{ display: "inline-flex", verticalAlign: "-3px", marginRight: 8 }}><Icon name="buzerak" size={20} color="var(--ember)" /></span>Šťuchnúť {target.name}</span>
          <button onClick={onClose} style={st.sheetClose} aria-label="Zavrieť"><Icon name="close" size={17} color="currentColor" /></button>
        </div>
        {BUZERAK_LINES.map((l) => (
          <button key={l} onClick={() => send(l)} disabled={busy} className="sheet-row" style={st.sheetRow}>
            <span style={{ flex: 1, textAlign: "left" }}><span style={st.sheetLabel}>{l}</span></span>
            <span style={st.sheetChevron}>›</span>
          </button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); if (custom.trim()) send(custom.trim()); }}
          style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input className="field" style={{ ...st.field, flex: 1 }} placeholder="Vlastná správa…"
            value={custom} onChange={(e) => setCustom(e.target.value)} maxLength={120} />
          <button type="submit" disabled={busy || !custom.trim()} style={{ ...st.primaryBtn, width: 46, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Šťuchnúť"><Icon name="buzerak" size={20} color="currentColor" /></button>
        </form>
        {err && <div style={st.err}>{err}</div>}
      </div>
    </div>
  );
}

// v32.18 — detail šťuchnutia (poke): otvorí sa klikom na poke správu v chate alebo na
// poke oznámenie. Pri prijatom šťuchnutí ponúkne „Šťuchnúť späť".
export function PokeModal({ text, fromName, fromId, canReply, onClose, onReplied }) {
  const [replying, setReplying] = useState(false);
  // v33h — „Šťuchnúť späť" NAHRADÍ tento modal poke-sheetom (nestacká sa naň). Predtým BuzerakSheet
  // otváral svoj scrim NAD stále otvoreným PokeModalom, takže jeho hlavička „Šťuchnutie" presvitala
  // vysivená. Teraz je naraz otvorený len jeden; zavretím sheetu sa vrátiš k detailu šťuchnutia.
  if (replying) {
    return <BuzerakSheet target={{ id: fromId, name: fromName }} onClose={() => setReplying(false)}
      onSent={() => { setReplying(false); if (onReplied) onReplied(); onClose(); }} />;
  }
  return (
    <Modal center title="Šťuchnutie" icon="buzerak" maxWidth={380} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textAlign: "center", padding: "2px 0" }}>
        <span className="shake" style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--ember-soft)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="buzerak" size={28} color="var(--ember)" />
        </span>
        {fromName && <div style={st.taskDesc}>{fromName} ťa štuchol/la</div>}
        <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, color: "var(--text)" }}>„{text}“</div>
      </div>
      {canReply && fromId != null && (
        <Button variant="primary" block style={{ marginTop: 16 }} onClick={() => setReplying(true)}>
          <Icon name="buzerak" size={16} color="currentColor" />Šťuchnúť späť
        </Button>
      )}
    </Modal>
  );
}
